import {
	forwardRef,
	useCallback,
	useEffect,
	useRef,
	useState,
	type ForwardedRef,
} from 'react'

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
	Input,
} from '#app/components/ui'
import { MIN_CHAR_LIMIT } from '#app/routes/address.ts'
import { useDebounceValue } from '#app/utils/misc.tsx'

export interface Address {
	value: {
		address: string
		lat: number
		lon: number
	}
	label: string
}

interface APIResponse {
	addresses: Address[]
}

export interface AddressSearchProps {
	id?: string
	defaultValue?: Address
	onSelect?: (address: Address) => void
	onInputChange?: (value: string) => void
}

export const AddressSearchCombobox = forwardRef(function AddressSearch(
	{ id, onSelect, onInputChange, defaultValue, ...props }: AddressSearchProps,
	ref: ForwardedRef<HTMLInputElement>,
) {
	const [open, setOpen] = useState(false)

	const [query, setQuery] = useState(defaultValue?.label || '')
	const [addresses, setAddresses] = useState<Address[]>([])
	const [isFetching, setIsFetching] = useState(false)
	const debouncedQuery = useDebounceValue(query, 500)
	const wrapperRef = useRef<HTMLDivElement>(null) // Ref for the wrapper element

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target as Node)
			) {
				setOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	useEffect(() => {
		if (debouncedQuery.length <= MIN_CHAR_LIMIT) {
			setAddresses([])
			return
		}

		setIsFetching(true)
		const fetchData = async () => {
			try {
				const response = await fetch('/address', {
					method: 'POST',
					body: new URLSearchParams({ address: debouncedQuery }),
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
					},
				})
				const data = (await response.json()) as APIResponse
				setAddresses(data.addresses)
			} catch (error) {
				console.error('Failed to fetch addresses:', error)
			} finally {
				setIsFetching(false)
			}
		}

		fetchData()
	}, [debouncedQuery])

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setQuery(event.target.value)
			onInputChange?.(event.target.value)
			setOpen(true)
		},
		[onInputChange],
	)

	const handleAddressChange = useCallback(
		(address: Address) => {
			setQuery(address.label)
			onSelect?.(address)
			setOpen(false)
		},
		[onSelect],
	)

	return (
		<div ref={wrapperRef}>
			<Command className="bg-transparent p-0.5" shouldFilter={false}>
				<Input
					ref={ref}
					value={query}
					onChange={handleInputChange}
					loading={isFetching}
					autoComplete="off"
					{...props}
				/>
				{open && debouncedQuery && (
					<CommandList>
						{debouncedQuery !== '' && (
							<>
								<CommandEmpty>No address found.</CommandEmpty>

								{!!addresses?.length && (
									<CommandGroup className="h-fit rounded-lg border shadow-md">
										{addresses.map((address, index) => (
											<CommandItem
												key={address.label + index}
												value={address.label}
												onSelect={() => handleAddressChange(address)}
											>
												{address.label}
											</CommandItem>
										))}
									</CommandGroup>
								)}
							</>
						)}
					</CommandList>
				)}
			</Command>
		</div>
	)
})
